import React, { useState } from 'react';
import { storage, firebase as firestore } from '../../services/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { logEvent, logError } from '../../utils/analytics';
import { convertToWebP } from '../../utils/imageUpload';
import './style.css';

const FeedbackForm = ({ onClose, feedbackType }) => {
  const [feedback, setFeedback] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const auth = getAuth();

  const uploadScreenshot = async (file) => {
    if (!auth.currentUser) return null;

    try {
      // Convert to WebP using the centralized function
      const webpBlob = await convertToWebP(file);
      const fileName = `bug-${Date.now()}.webp`;
      const storageRef = ref(storage, `feedback/${auth.currentUser.uid}/bug/${fileName}`);

      // Upload the WebP file
      await uploadBytes(storageRef, webpBlob);
      const downloadURL = await getDownloadURL(storageRef);

      logEvent('screenshot_uploaded', {
        feedback_type: 'bug',
        file_name: fileName
      });

      return downloadURL;
    } catch (error) {
      logError('FeedbackForm: upload screenshot', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      let screenshotUrl = null;
      if (screenshot && auth.currentUser) {
        setUploadProgress(30);
        screenshotUrl = await uploadScreenshot(screenshot);
        setUploadProgress(70);
      }

      const feedbackData = {
        type: feedbackType,
        content: feedback,
        screenshotUrl,
        userId: auth.currentUser?.uid || null,
        userEmail: auth.currentUser?.email || null,
        timestamp: serverTimestamp(),
        status: 'new'
      };

      await addDoc(collection(firestore, 'feedback'), feedbackData);
      setUploadProgress(100);

      logEvent('feedback_submitted', {
        type: feedbackType,
        has_screenshot: !!screenshotUrl
      });

      setSubmitSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      logError('FeedbackForm: submit feedback', error);
      setUploadProgress(0);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTitle = () => {
    switch (feedbackType) {
      case 'bug':
        return 'Report a Bug';
      case 'other':
        return 'Other Feedback';
      default:
        return 'Submit Feedback';
    }
  };

  const getPlaceholder = () => {
    switch (feedbackType) {
      case 'bug':
        return 'Please describe the issue you encountered...';
      case 'other':
        return 'Please share your thoughts...';
      default:
        return 'Please provide your feedback...';
    }
  };

  const getIcon = () => {
    switch (feedbackType) {
      case 'bug':
        return '🐛';
      case 'other':
        return '📝';
      default:
        return '💬';
    }
  };

  return (
    <>
      <div className="feedback-overlay" onClick={onClose} />
      <div className="feedback-form">
        <div className="feedback-form-header">
          <span className="feedback-form-icon">{getIcon()}</span>
          <h2 className="feedback-form-title">{getTitle()}</h2>
        </div>

        {submitSuccess ? (
          <div className="feedback-form-success">
            <p>Thank you for your feedback! We'll look into this.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="feedback-form-field">
              <label className="feedback-form-label">
                Description
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                required
                className="feedback-form-textarea"
                placeholder={getPlaceholder()}
              />
            </div>

            {feedbackType === 'bug' && (
              <div className="feedback-form-field">
                <label className="feedback-form-label">
                  Screenshot (Optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setScreenshot(file);
                    }
                  }}
                  className="feedback-form-file-input"
                />
                {screenshot && (
                  <div className="feedback-form-file-preview">
                    <p>Selected file: {screenshot.name}</p>
                  </div>
                )}
              </div>
            )}

            {isSubmitting && (
              <div className="feedback-form-progress">
                <div
                  className="feedback-form-progress-bar"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
                <p>Submitting feedback... {uploadProgress}%</p>
              </div>
            )}

            <div className="feedback-form-actions">
              <button
                type="button"
                onClick={onClose}
                className="feedback-form-button feedback-form-button-cancel"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="feedback-form-button feedback-form-button-submit"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default FeedbackForm;